import React, { useState, useEffect } from "react"
import { makeStyles } from "@material-ui/styles"
import { Box, useMediaQuery } from "@material-ui/core"
import moment from "moment"
import { ActionCard } from "./ActionCard"
import {
  Checkbox,
  Icon,
  DownloadList,
  ImagesViewer,
  AddPhotoStep,
  OutlinedInputStep,
  YesNoNaStep,
  NumberRatingStep,
} from ".."
import { MentionOutput } from "../Mentions"
import { TASK_RESPONSE_TYPE, useMakeJobStepOptimistic, useMakeOptimistic, useMutationJobStepComplete } from "../../data"
import { DatePicker } from "../DateTimePickers"
import { mapToIds, preventDefault, toId, useFocus } from "../../utils"
import { ColumnBox, RowBox } from "../Boxes"
import { CompletedByAvatar } from "../Avatar"
import FooterButton from "../Buttons/ActionCardFooterButton"
import AddNoteFooterButton from "../JobViewer/AddNoteFooterButton"
import RaiseActionFooterButton from "../JobViewer/RaiseActionFooterButton"

const useStyles = makeStyles((theme) => ({
  title: {
    fontSize: 16,
    fontWeight: "500",
    lineHeight: "20px",
    color: theme.palette.text.main,
    marginTop: 19,
  },
  description: {
    fontSize: 14,
    lineHeight: "20px",
    color: theme.palette.text.main,
  },
  circle: {
    backgroundColor: ({ complete }) => (complete ? "transparent" : "#f5fffc"),
    borderRadius: 21,
    width: 42,
    height: 42,
  },
  divider: {
    marginLeft: ({ complete, skip }) => (complete || skip ? 0 : -2),
    marginRight: ({ complete, skip }) => (complete || skip ? 0 : -2),
    width: "100%",
    backgroundColor: ({ complete, skip }) => theme.palette.cards.divide[complete || skip ? "darker" : "normal"],
  },
  icon: {
    color: ({ complete }) => theme.palette.cards.icons[complete ? "complete" : "incomplete"],
    padding: 8,
    fontSize: 42,
  },
  promptArea: {
    minHeight: 64,
  },
  button: {
    lineHeight: "14px",
    height: 36,
    paddingLeft: 0,
    paddingRight: 0,
    minWidth: "auto",
    "&:not(.MuiButton-containedPrimary):not(.Mui-disabled)": {
      backgroundColor: theme.palette.buttons.light.background,
    },
  },
  completed: {
    color: theme.palette.success.main,
  },
  completedText: {
    color: "rgba(0,0,0,0.87)",
    fontWeight: "500",
  },
  completedButton: {
    backgroundColor: theme.palette.step.button.background.complete,
  },
  incompletedButton: {
    backgroundColor: theme.palette.step.button.background.incomplete,
  },
  footer: {
    padding: `${theme.spacing(1.25)}px ${theme.spacing(2)}px`,
  },
}))

const { NONE, NUMBER, TEXT, DATE, PHOTOS, YES_NO_NA, NUMBER_RATING_5, NUMBER_RATING_10 } = TASK_RESPONSE_TYPE

const ProcessStepActionCard = ({ job, step, isScheduled, isPreview, onCreateAction, cy = "ProcessStep" }) => {
  const jobID = toId(job)
  const { id, name, description, uploads, skip, completedAt, completedBy, responseType, response, responseUploads } =
    step
  const xs = useMediaQuery((theme) => theme.breakpoints.down("xs"))
  const [completeJobStep] = useMutationJobStepComplete(job)
  const { currentUser: makeOptimisticCurrentUser } = useMakeOptimistic()
  const { makeJobStepCompleteOptimisticResponse } = useMakeJobStepOptimistic()
  const [complete, setComplete] = useState(completedAt != null)
  const [skipped, setSkipped] = useState(skip)
  const [completeAt, setCompleteAt] = useState(completedAt)
  const [completeBy, setCompleteBy] = useState(completedBy)
  const [inputRef, setInputFocus] = useFocus()
  const [newResponse, setNewResponse] = useState(response)
  const classes = useStyles({ complete, skip })

  useEffect(() => {
    const isComplete = completedAt != null
    setComplete(completedAt != null)
    setCompleteAt(completedAt)
    setCompleteBy(completedBy)
    setSkipped(skip)
    if (isComplete) {
      setNewResponse(response || "")
    }
  }, [completedAt, skip, response, completedBy])

  const makeOptimisticResponse = (variables) =>
    makeJobStepCompleteOptimisticResponse({
      ...variables,
      job,
    })

  const handleCreateAction = () => {
    onCreateAction && onCreateAction(step)
  }

  const handleCompletedChange = async (event, skipping) => {
    const state = !complete
    setComplete(state)
    setCompleteAt(state ? moment().format() : null)
    setCompleteBy(makeOptimisticCurrentUser())
    setSkipped(skipping || false)
    preventDefault(event, async () => {
      if (!isPreview) {
        const variables = {
          job: jobID,
          step: id,
          skip: skipping,
          complete: state,
          response: newResponse,
        }

        await completeJobStep({
          variables,
          optimisticResponse: makeOptimisticResponse(variables),
        })
      }
      if (!state && [NUMBER, TEXT].includes(responseType)) setInputFocus()
    })
  }

  const handleResponseChange = (event) => {
    const { value } = event.target

    setNewResponse(value)
  }

  const handleButtonResponseClick = async (event, value) => {
    const clearing = !!value && value === newResponse
    setNewResponse(clearing ? "" : value)
    setCompleteAt(!clearing ? moment().format() : null)
    setCompleteBy(makeOptimisticCurrentUser())
    preventDefault(event, async () => {
      const state = !clearing
      setComplete(state)
      if (!isPreview) {
        const variables = {
          job: jobID,
          step: id,
          skip: false,
          complete: state,
          response: clearing ? "" : value,
        }

        await completeJobStep({
          variables,
          optimisticResponse: makeOptimisticResponse(variables),
        })
      }
    })
  }

  const handleUploadsChange = async (event, value) => {
    preventDefault(event, async () => {
      setComplete(true)
      if (!isPreview) {
        await completeJobStep({
          variables: {
            job: jobID,
            step: id,
            skip: false,
            complete: !!value.length,
            responseUploads: value ? [...mapToIds(value)] : [],
          },
        })
      }
    })
  }

  const handleUploadsRemove = async (value) => {
    if (!isPreview) {
      await completeJobStep({
        variables: {
          job: jobID,
          step: id,
          skip: false,
          complete: false,
          responseUploads: value ? [...mapToIds(value)] : [],
        },
      })
    }
  }

  const handleUploadsEdit = async (event) => {
    preventDefault(event, async () => {
      setComplete(false)
      if (!isPreview) {
        await completeJobStep({
          variables: {
            job: jobID,
            step: id,
            skip: false,
            complete: false,
          },
        })
      }
    })
  }

  return (
    <ActionCard
      title={
        <ColumnBox>
          <Box className={classes.title} mb={description || uploads?.length ? 0 : 2}>
            {name}
          </Box>
          {description && (
            <Box mt={1} mb={uploads?.length ? 0 : 2} mr={2} className={classes.description}>
              <MentionOutput content={description} />
            </Box>
          )}
          {!!uploads?.length && (
            <Box mb={2}>
              <DownloadList uploads={uploads} />
              <ImagesViewer uploads={uploads} />
            </Box>
          )}
        </ColumnBox>
      }
      titleProps={{
        alignSelf: "flex-start",
      }}
      cursor="default"
      contentMinHeight="auto"
      completed={complete}
      skipped={skipped}
      isScheduled={isScheduled}
      classes={{ footer: classes.footer }}
      stub={
        <Box p={1} alignSelf="flex-start">
          <Box className={classes.circle}>
            {responseType === NONE && !skipped && (
              <Checkbox
                type="complete"
                checked={complete}
                onClick={handleCompletedChange}
                disabled={isScheduled}
                data-cy={`ProcessStep-field-${responseType}`}
              />
            )}
            {responseType === NONE && skipped && (
              <Checkbox
                type="skipped"
                checked
                onClick={handleCompletedChange}
                disabled
                data-cy={`ProcessStep-field-${responseType}-skip`}
              />
            )}
            {responseType === PHOTOS && <Icon name="camera" className={classes.icon} />}
            {[NUMBER_RATING_5, NUMBER_RATING_10].includes(responseType) && (
              <Icon name="rating" className={classes.icon} />
            )}
            {![NONE, PHOTOS, NUMBER_RATING_5, NUMBER_RATING_10].includes(responseType) && (
              <Icon name="text" className={classes.icon} />
            )}
          </Box>
        </Box>
      }
      rightChildren={
        complete && (
          <CompletedByAvatar at={completeAt} by={completeBy} ml="auto" pt={2} pr={2} pl={1} alignSelf="flex-start" />
        )
      }
      footer={
        <RowBox flexGrow={1}>
          <Box mr={2}>
            {!skipped && (
              <FooterButton
                icon="skip"
                label={xs ? "Skip" : "Skip step"}
                onClick={(event) => handleCompletedChange(event, true)}
                disabled={complete || isPreview || isScheduled}
              />
            )}
            {skipped && (
              <FooterButton icon="unskip" label={xs ? "Unskip" : "Unskip step"} onClick={handleCompletedChange} />
            )}
          </Box>
          <RaiseActionFooterButton
            job={toId(job)}
            step={step}
            isPreview={isPreview}
            isScheduled={isScheduled}
            onCreateAction={handleCreateAction}
          />
          <AddNoteFooterButton job={toId(job)} step={step} isPreview={isPreview} isScheduled={isScheduled} />
        </RowBox>
      }
      p="auto"
      cy={cy}
    >
      {[NUMBER, TEXT].includes(responseType) && (
        <OutlinedInputStep
          expand={!skipped}
          inputRef={inputRef}
          complete={complete}
          step={step}
          onResponseChange={handleResponseChange}
          onCompletedChange={handleCompletedChange}
          value={newResponse}
          classes={{
            divider: classes.divider,
            completedText: classes.completedText,
            incompletedButton: classes.incompletedButton,
            completedButton: classes.completedButton,
          }}
        />
      )}
      {DATE === responseType && <DatePicker value={response} disabled={isScheduled} />}
      {PHOTOS === responseType && (
        <AddPhotoStep
          expand={!skipped}
          responseUploads={responseUploads}
          onChange={handleUploadsChange}
          onRemove={handleUploadsRemove}
          onEdit={handleUploadsEdit}
          classes={{
            button: classes.button,
            divider: classes.divider,
            incompletedButton: classes.incompletedButton,
            completedButton: classes.completedButton,
            promptArea: classes.promptArea,
          }}
          complete={complete}
          disabled={isScheduled || isPreview}
        />
      )}
      {YES_NO_NA === responseType && (
        <YesNoNaStep
          expand={!skipped}
          value={newResponse}
          onClick={handleButtonResponseClick}
          buttonClass={classes.button}
          disabled={isScheduled || isPreview}
          classes={{
            divider: classes.divider,
          }}
        />
      )}
      {[NUMBER_RATING_5, NUMBER_RATING_10].includes(responseType) && (
        <NumberRatingStep
          expand={!skipped}
          value={newResponse}
          count={responseType === NUMBER_RATING_5 ? 5 : 10}
          onClick={handleButtonResponseClick}
          buttonClass={classes.button}
          disabled={isScheduled || isPreview}
          classes={{
            divider: classes.divider,
          }}
        />
      )}
    </ActionCard>
  )
}

export { ProcessStepActionCard }
